import type { NodeLogoKey } from './types';

type Space = 'professional' | 'investor' | 'templates';

/**
 * Constructs a URL for fetching firm content from S3.
 *
 * @param {string} firmId - The firm's ID.
 * @param {string} nodeId - The node's ID.
 * @param {Space} space - The S3 space the content lives in.
 * @param {string} fileName - The file name of the firm content.
 * @returns {string} - Constructed URL for fetching the firm content.
 */
export const getFirmContentUrl = (firmId: string, nodeId: string, space: Space, fileName: string): string =>
  `https://${process.env.BUCKET_NAME}.${process.env.AWS_BUCKET_URL}/${firmId}/${nodeId}/${space}/${fileName}`;

/** Maps each logical logo type to its S3 space and file name. */
const NODE_LOGO_PATHS: Record<NodeLogoKey, { space: Space; fileName: string }> = {
  favicon: { space: 'professional', fileName: 'favicon.jpg' },
  headerLogo: { space: 'professional', fileName: 'nav-logo.png' },
  professionalLogo: { space: 'professional', fileName: 'login-logo.png' },
  investorLogo: { space: 'investor', fileName: 'header-logo.png' },
  templateLogo: { space: 'templates', fileName: 'logo.png' },
};

/**
 * Returns the S3 base URL (`https://{bucket}.{host}/{firmId}`) for a firm's content.
 *
 * Reads the server-only bucket config, so this must run server-side (e.g. in branding SSR).
 * The result is passed to the client, which builds per-node logo URLs from it via
 * {@link getNodeLogoUrl} without needing the bucket config itself.
 *
 * @param {string} firmId - The firm's ID.
 * @returns {string} - The firm's S3 base URL.
 */
export const getLogoBaseUrl = (firmId: string): string =>
  `https://${process.env.BUCKET_NAME}.${process.env.AWS_BUCKET_URL}/${firmId}`;

/**
 * Builds the canonical S3 URL for a node's own logo of the given type.
 *
 * Always targets the exact node supplied, so callers (e.g. upload polling) never accidentally
 * target a parent/inherited node that happens to be the current display source.
 *
 * @param {string} baseUrl - The firm's S3 base URL (see {@link getLogoBaseUrl}).
 * @param {string} nodeId - The node's ID.
 * @param {NodeLogoKey} logoKey - The logical logo type.
 * @returns {string} - The node's own logo URL.
 */
export const getNodeLogoUrl = (baseUrl: string, nodeId: string, logoKey: NodeLogoKey): string => {
  const { space, fileName } = NODE_LOGO_PATHS[logoKey];
  return `${baseUrl}/${nodeId}/${space}/${fileName}`;
};

/**
 * Appends a `?lastmod=` cache-busting query parameter when a timestamp is supplied.
 *
 * @param {string} url - The logo URL.
 * @param {string} [lastModified] - Cache-busting timestamp.
 * @returns {string} - The URL, cache-busted when `lastModified` is provided.
 */
export const withLastMod = (url: string, lastModified?: string): string =>
  lastModified ? `${url}?lastmod=${lastModified}` : url;

/**
 * Builds a node's own logo URL, cache-busted when a timestamp is supplied.
 *
 * @param {string} baseUrl - The firm's S3 base URL (see {@link getLogoBaseUrl}).
 * @param {string} nodeId - The node's ID.
 * @param {NodeLogoKey} logoKey - The logical logo type.
 * @param {string} [lastModified] - Cache-busting timestamp.
 * @returns {string} - The node's own, optionally cache-busted, logo URL.
 */
export const buildLogoUrl = (baseUrl: string, nodeId: string, logoKey: NodeLogoKey, lastModified?: string): string =>
  withLastMod(getNodeLogoUrl(baseUrl, nodeId, logoKey), lastModified);
